import { cva, type VariantProps } from "class-variance-authority";
import {
  BiRegularDownload,
  BiRegularFile,
  BiRegularShow,
  BiSolidTrash,
} from "solid-icons/bi";
import { type Component, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { useAssetDownload } from "@/domains/documents/hooks/use-asset-download";
import type { PrivateAssetDto } from "@/domains/documents/types";
import { formatAssetDate, formatAssetFileSize } from "@/libs/assets";
import { cn } from "@/libs/cn";
import type { Asset } from "@/types";

/** Private assets download via signed URL; public ones keep `public_url`. */
const isPrivateAsset = (asset: Asset): asset is PrivateAssetDto =>
  asset.type === "RESUME" || asset.type === "APPLICATION_ATTACHMENT";

const documentCardVariants = cva(
  "flex items-center justify-between rounded-lg border border-gray-200 bg-white",
  {
    variants: {
      size: {
        small: "p-2 gap-2",
        default: "p-4 gap-3",
        large: "p-6 gap-4",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const documentCardIconContainerVariants = cva(
  "flex items-center justify-center bg-gray-100 text-gray-600",
  {
    variants: {
      size: {
        small: "w-8 h-8 rounded",
        default: "w-10 h-10 rounded-lg",
        large: "w-12 h-12 rounded-lg",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const documentCardIconVariants = cva("", {
  variants: {
    size: {
      small: "w-4 h-4",
      default: "w-5 h-5",
      large: "w-6 h-6",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const documentCardFilenameVariants = cva("truncate font-medium text-gray-900", {
  variants: {
    size: {
      small: "text-sm",
      default: "text-base",
      large: "text-lg",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const documentCardMetadataVariants = cva("text-gray-500", {
  variants: {
    size: {
      small: "text-xs",
      default: "text-sm",
      large: "text-base",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

type DocumentCardProps = {
  asset: Asset;
  size?: VariantProps<typeof documentCardVariants>["size"];
  onDelete?: () => void;
  onView?: () => void;
  onDownload?: () => void;
  class?: string;
};

export const DocumentCard: Component<DocumentCardProps> = (props) => {
  const { t } = useI18n();
  const size = () => props.size ?? "default";
  const { download, isPending } = useAssetDownload();

  // Metadata that is missing on historical assets is omitted rather than
  // rendered as "NaN KB" / "null •" (AC 13). The upload date is always shown
  // (present as `Date` on public assets, ISO string on private ones).
  const metadataLabel = () => {
    const size_bytes = props.asset.size_bytes;
    const parts: string[] = [];
    if (size_bytes != null) parts.push(formatAssetFileSize(size_bytes));
    parts.push(formatAssetDate(props.asset.created_at));
    return parts.join(" • ");
  };

  const handleView = () => {
    if (props.onView) {
      props.onView();
      return;
    }
    const asset = props.asset;
    if (!isPrivateAsset(asset)) {
      window.open(asset.public_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownload = () => {
    if (props.onDownload) {
      props.onDownload();
      return;
    }
    const asset = props.asset;
    if (!isPrivateAsset(asset)) {
      const link = document.createElement("a");
      link.href = asset.public_url;
      link.download = asset.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDelete = () => {
    if (props.onDelete) {
      props.onDelete();
    }
  };

  return (
    <div class={cn(documentCardVariants({ size: size() }), props.class)}>
      {/* Left Section: Document Info */}
      <div class="flex min-w-0 flex-1 items-center gap-3">
        {/* Document Icon */}
        <div class={documentCardIconContainerVariants({ size: size() })}>
          <BiRegularFile class={documentCardIconVariants({ size: size() })} />
        </div>

        {/* Filename and Metadata */}
        <div class="min-w-0 flex-1">
          <p class={documentCardFilenameVariants({ size: size() })}>
            {props.asset.filename}
          </p>
          <p class={documentCardMetadataVariants({ size: size() })}>
            {metadataLabel()}
          </p>
        </div>
      </div>

      {/* Right Section: Action Icons */}
      <div class="flex items-center gap-2">
        <Show
          when={isPrivateAsset(props.asset)}
          fallback={
            <>
              {/* Public asset: view + download via public_url (invariato). */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleView}
                aria-label="View document"
              >
                <BiRegularShow />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDownload}
                aria-label="Download document"
              >
                <BiRegularDownload />
              </Button>
            </>
          }
        >
          {/* Private asset: a single "Scarica" action via signed URL. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => download(props.asset.id)}
            disabled={isPending(props.asset.id)}
            aria-busy={isPending(props.asset.id)}
            aria-label={t("common.download")}
          >
            <BiRegularDownload />
          </Button>
        </Show>

        {/* Delete Icon - Only shown if onDelete is provided */}
        {props.onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            class="text-red-500 hover:text-red-600"
            aria-label="Delete document"
          >
            <BiSolidTrash />
          </Button>
        )}
      </div>
    </div>
  );
};
